import {useEffect, useRef, useState} from "react";
import './CompletionExamRoom.scss';
import Layout from "../Layout/Layout";
import {useCommonStore} from "../../hooks/useCommonStore";
import api from "../../utils/api";
import {useNavigate, useParams} from "react-router-dom";
import cn from "classnames";
import {FormattedMessage, useIntl} from "react-intl";

const CompletionExamRoom = () => {
  const {id} = useParams();
  const {startLoading, stopLoading} = useCommonStore();
  const intl = useIntl();
  const intervalRef = useRef(null);
  const navigate = useNavigate();

  const [quizIdx, setQuizIdx] = useState(0);
  const [quizArr, setQuizArr] = useState([]);
  const [answerArr, setAnswerArr] = useState([]);
  const [currentQuiz, setCurrentQuiz] = useState({});
  const [remainTime, setRemainTime] = useState('00 : 00 : 00');
  const [isLastQuiz, setIsLastQuiz] = useState(false);

  useEffect(() => {
    const startExam = async() => {
      startLoading();
      const url = `/v1/user-exam/scheduled/${id}`;

      // 시험 정보
      const {examArchived, duration} = await api.get(url);
      const {examDto: {quizzes}} = examArchived[0];

      // 유저 답안
      const {startAt, userAnswer} = await api.get(url + '/user-data');

      if (userAnswer) {
        setAnswerArr(userAnswer);

        for (let i = 0; i < quizzes.length; i++) {
          if (!userAnswer[i] || userAnswer[i].length === 0) {
            setQuizIdx(i);
            break;
          }
        }
      }

      const dueDt = new Date(startAt);
      dueDt.setMinutes(dueDt.getMinutes() + Number(duration));

      const updateRemainingTime = async() => {
        const nowDt = new Date();
        const timeDifference = dueDt - nowDt;

        if (timeDifference < 0) {
          // 시간 초과
          alert(intl.formatMessage({id: '@exam_room_time_ended'}));
          window.location.href = '/main';
        } else {
          const hours = Math.floor(timeDifference / (1000 * 60 * 60));
          const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

          const formattedTime = `${String(hours).padStart(2, '0')} : ${String(minutes).padStart(2, '0')} : ${String(seconds).padStart(2, '0')}`;
          setRemainTime(formattedTime);
        }
      }

      updateRemainingTime();
      intervalRef.current = setInterval(updateRemainingTime, 1000);

      setQuizArr(quizzes);
      stopLoading();
    }
    startExam();

    return () => {
      clearInterval(intervalRef.current);
    }
  }, []);

  useEffect(() => {
    setCurrentQuiz(quizArr[quizIdx]?.quizI18n[0]);
    setIsLastQuiz(quizIdx === quizArr.length - 1);
  }, [quizArr, quizIdx]);

  const handleSelectChoice = (choiceNo) => {
    const choiceStr = choiceNo.toString();
    const nextArr = [...answerArr];

    if (!nextArr[quizIdx]) {
      nextArr[quizIdx] = [];
    }

    if (!nextArr[quizIdx].includes(choiceStr)) {
      nextArr[quizIdx].push(choiceStr);
    } else {
      nextArr[quizIdx] = nextArr[quizIdx].filter(element => element !== choiceStr);
    }

    setAnswerArr(nextArr);
  };

  const handlePrevQuiz = () => {
    saveUserAnswer(false);
    if (quizIdx !== 0) {
      setQuizIdx(quizIdx - 1);
    }
  }

  const handleNextQuiz = () => {
    if (!answerArr[quizIdx] || answerArr[quizIdx].length === 0) {
      alert(intl.formatMessage({id: '@exam_room_alert_no_answer'}));
      return;
    }
    saveUserAnswer(false);
    if (quizIdx < quizArr.length) {
      setQuizIdx(quizIdx + 1);
    }
  }

  const handleSubmitQuiz = async() => {
    if (!answerArr[quizIdx] || answerArr[quizIdx].length === 0) {
      alert(intl.formatMessage({id: '@exam_room_alert_no_answer'}));
      return;
    }

    if (window.confirm(intl.formatMessage({id: '@exam_room_submit_confirm'}))) {
      const data = await saveUserAnswer(true);
      if (data) {
        alert(intl.formatMessage({id: '@exam_room_submit_success'}));
        navigate('/main');
      }
    }
  }

  const saveUserAnswer = async(isSubmit) => {
    let userAnswer = [];
    for (let i = 0; i < quizArr.length; i++) {
      userAnswer.push(answerArr[i] || []);
    }
    return await api.put(`/v1/user-exam/scheduled/${id}/user-data`, {userAnswer, isSubmit});
  }
  
  return (
    <Layout>
      <div className='CompletionExamRoom'>
        <section className='progress'>
          <div className='progress-item'>
            <span className='label'>
              <FormattedMessage id='@exam_room_time_remaining' />
            </span>
            <span className='time'>{remainTime}</span>
          </div>
          <div className='progress-item'>
            <span className='label'>
                <FormattedMessage id='@exam_room_progress' />
            </span>
            <span className='count'>
              <span className='quiz-no'>{quizIdx + 1}</span>/{quizArr.length}
            </span>
            <div className='bars'>
              <div className='empty-bar' />
              <div
                className='filled-bar'
                style={{width: ((quizIdx + 1) / quizArr.length * 100).toString() + '%'}}
              />
            </div>
          </div>
        </section>

        <section className='quiz'>
          <div className='title'>
            <span className='no'>{quizIdx + 1}.</span>
            <span dangerouslySetInnerHTML={{ __html: currentQuiz?.title }} />
          </div>
          {
            currentQuiz?.context?.choices.map((choice, i) => {
              const isChecked = answerArr[quizIdx]?.includes((i+1).toString());
              return (
                <div
                  key={i}
                  className={cn('choice', {'is-checked': isChecked})}
                  onClick={() => handleSelectChoice(i+1)}
                >
                  {isChecked && <div className='checkmark'>&#10003;</div>}
                  <div className='no'>{i+1}</div> {choice}
                </div>
              );
            })
          }
        </section>

        <section className='buttons'>
          {
            quizIdx !== 0 &&
            <button
              className='prev'
              onClick={handlePrevQuiz}
            >
              <FormattedMessage id='@exam_room_prev_page' />
            </button>
          }
          {
            !isLastQuiz ?
              <button
                className='next'
                onClick={handleNextQuiz}
              >
                <FormattedMessage id='@exam_room_next_page' />
              </button>
              :
              <button
                className='next'
                onClick={handleSubmitQuiz}
              >
                <FormattedMessage id='@exam_room_submit_btn' />
              </button>
          }
        </section>
      </div>
    </Layout>
  );
}

export default CompletionExamRoom;